/**
 * Oracle Resolver — `PKG.▮` member candidates.
 *
 * Kept out of `oracleResolver.ts` (already ~3.8k lines) because it owns two
 * independent decisions:
 *
 *   1. How an `OraclePackageMemberKind` maps onto a `CompletionCandidateKind`
 *      + insert text. Subprograms insert as a call snippet and trigger
 *      signature help; a constant / variable / type / cursor / exception
 *      inserts as a bare name — `pkg.c_default_currency` is an operand, not a
 *      call. All of them share `detail` as the disambiguator, since the
 *      candidate kind union has no separate procedure / function member.
 *
 *   2. Whether the caret is at a PL/SQL STATEMENT position. There, a
 *      PROCEDURE is the only member that can legally stand alone, so
 *      procedures lead the list (`Docs/AutocompleteTestScripts/oracle.md`
 *      script 35 §5 — `log_review` ranked above `compute_bonus` inside
 *      `BEGIN … END;`). At an expression position (script 35 §4, inside a
 *      SELECT list) no such claim holds and the order stays alphabetical.
 *
 * Pure — no Monaco / electron / React imports.
 */

import type {
  CompletionCandidate,
  CompletionCandidateKind,
} from '@/core/engines/intellisenseTypes';
import type {
  OraclePackageMember,
  OraclePackageMemberKind,
  OracleSchemaPackage,
} from '@/features/connections/services/language-services/oracleSchemaTypes';
import { formatIdentifier } from './oracleIdentifiers';

/** Members that insert as a call (`NAME($0)`) and trigger signature help. */
const CALLABLE_KINDS: ReadonlySet<OraclePackageMemberKind> = new Set([
  'procedure',
  'function',
  // A member known only from ALL_PROCEDURES IS a subprogram — the kind is
  // unknown, callability is not.
  'unknown',
]);

const MEMBER_CANDIDATE_KIND: Readonly<
  Record<OraclePackageMemberKind, CompletionCandidateKind>
> = {
  procedure: 'routine',
  function: 'routine',
  unknown: 'routine',
  // A package constant / variable is a value reference, closest to the
  // resolver's own PL/SQL local-variable candidates.
  constant: 'variable',
  variable: 'variable',
  // No 'type' / 'cursor' / 'exception' member exists in the candidate-kind
  // union; 'system-object' is its named-schema-object slot (the same kind the
  // resolver already uses for a package name itself).
  type: 'system-object',
  cursor: 'system-object',
  exception: 'system-object',
};

const MEMBER_DETAIL_NOUN: Readonly<Record<OraclePackageMemberKind, string>> = {
  procedure: 'procedure',
  function: 'function',
  constant: 'constant',
  variable: 'variable',
  type: 'type',
  cursor: 'cursor',
  exception: 'exception',
  unknown: 'member',
};

/**
 * Statement-position order: procedures first, then the other callables, then
 * everything a bare statement cannot be. Applied ONLY at a statement
 * position — elsewhere `orderHint` is left unset so the ranker's alphabetical
 * tiebreak decides.
 */
function statementOrderHint(kind: OraclePackageMemberKind): number {
  if (kind === 'procedure') return 0;
  if (CALLABLE_KINDS.has(kind)) return 1;
  return 2;
}

function memberCandidate(
  member: OraclePackageMember,
  packageName: string,
  proceduresFirst: boolean,
): CompletionCandidate {
  const callable = CALLABLE_KINDS.has(member.kind);
  return {
    label: member.name,
    insertText: callable ? `${member.name}($0)` : formatIdentifier(member.name),
    isSnippet: callable,
    kind: MEMBER_CANDIDATE_KIND[member.kind],
    tier: 1,
    detail: `${packageName} ${MEMBER_DETAIL_NOUN[member.kind]}`,
    filterText: member.name,
    ...(callable ? { triggerSignatureHelp: true } : {}),
    ...(proceduresFirst ? { orderHint: statementOrderHint(member.kind) } : {}),
  };
}

/**
 * `PKG.▮` — the package's public members, pinned to tier 1 so no leaking
 * column or table outranks them.
 */
export function buildOraclePackageMemberCandidates(
  pkg: OracleSchemaPackage,
  options?: { readonly proceduresFirst?: boolean },
): CompletionCandidate[] {
  const proceduresFirst = options?.proceduresFirst === true;
  return pkg.members.map((member) =>
    memberCandidate(member, pkg.name, proceduresFirst),
  );
}

/**
 * `PKG.▮` for a curated built-in SYS package (`DBMS_OUTPUT`, `UTL_FILE`, …)
 * whose member list is a bare name array — every entry is a subprogram.
 */
export function buildOracleBuiltinPackageMemberCandidates(
  packageName: string,
  memberNames: readonly string[],
): CompletionCandidate[] {
  return memberNames.map((name) => ({
    label: name,
    insertText: `${name}($0)`,
    isSnippet: true,
    kind: 'routine' as const,
    tier: 1 as const,
    detail: `${packageName} built-in member`,
    filterText: name,
    triggerSignatureHelp: true,
  }));
}

/**
 * True when the dotted base immediately before `offset` starts a PL/SQL
 * STATEMENT rather than sitting inside an expression.
 *
 * The test is the token that precedes the dotted base: a statement separator
 * or block opener (`BEGIN`, `;`, `THEN`, `ELSE`, `LOOP`, `DECLARE`, `>>`
 * label close) means the next thing written has to be a statement — so a
 * procedure call. `SELECT`, an operator, `(`, `,`, `:=` and friends mean the
 * position takes a value.
 *
 * Text-based on purpose: PL/SQL bodies sit outside the SQL grammar's reach,
 * which is why the resolver's package-dot arms are reached with
 * `positionKind: 'unknown'` in the first place.
 */
const STATEMENT_LEAD_WORDS: ReadonlySet<string> = new Set([
  'BEGIN',
  'DECLARE',
  'THEN',
  'ELSE',
  'LOOP',
  'DO',
]);

const DOTTED_BASE_CHAR = /[A-Za-z0-9_$#."]/;

export function isOraclePackageStatementPosition(
  source: string,
  offset: number,
): boolean {
  // Walk back over the dotted path the caret sits in (`acact_pkg_payroll.`).
  let i = Math.min(offset, source.length);
  while (i > 0 && DOTTED_BASE_CHAR.test(source[i - 1]!)) i--;
  // Then over whitespace and comments to the previous significant token.
  i = skipTriviaBackwards(source, i);
  if (i <= 0) return true;
  const prev = source[i - 1]!;
  if (prev === ';') return true;
  if (prev === '>' && source[i - 2] === '>') return true; // `<<label>>`
  if (!/[A-Za-z0-9_$#]/.test(prev)) return false;
  let wordEnd = i;
  let wordStart = i;
  while (wordStart > 0 && /[A-Za-z0-9_$#]/.test(source[wordStart - 1]!)) wordStart--;
  return STATEMENT_LEAD_WORDS.has(source.slice(wordStart, wordEnd).toUpperCase());
}

/** Skip whitespace plus `--` line and block comments, walking backwards. */
function skipTriviaBackwards(source: string, from: number): number {
  let i = from;
  for (;;) {
    while (i > 0 && /\s/.test(source[i - 1]!)) i--;
    if (i >= 2 && source[i - 2] === '*' && source[i - 1] === '/') {
      let j = i - 2;
      while (j > 1 && !(source[j - 2] === '/' && source[j - 1] === '*')) j--;
      i = Math.max(j - 2, 0);
      continue;
    }
    // A `--` comment ends at a newline, which the whitespace skip above
    // already consumed; walk back to its start when one is there.
    const lineStart = source.lastIndexOf('\n', Math.max(i - 1, 0)) + 1;
    const lineHead = source.slice(lineStart, i);
    const commentAt = lineHead.indexOf('--');
    if (commentAt >= 0 && i > lineStart + commentAt) {
      i = lineStart + commentAt;
      continue;
    }
    return i;
  }
}
